import React from 'react';
import i18n from '../../i18n';
import LocationName from '../LocationName/index.jsx';
import getLocationType from '../../actions/getLocationType';
import getSelectedLocationName from '../../actions/getSelectedLocationName';
import { useAppContext } from '../../context/AppState.jsx';

const firstNonEmptyString = (...values) =>
	values.find((value) => typeof value === 'string' && value.trim().length > 0) || null;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const replaceTokensWithElements = (templateString, replacements, keyPrefix) => {
	if (!templateString) return null;
	const tokens = Object.keys(replacements);
	if (tokens.length === 0) return templateString;

	const pattern = new RegExp(tokens.map((token) => escapeRegExp(token)).join('|'), 'g');
	const parts = [];
	let lastIndex = 0;
	let elementIndex = 0;

	templateString.replace(pattern, (matched, offset) => {
		if (offset > lastIndex) {
			parts.push(templateString.slice(lastIndex, offset));
		}

		const element = replacements[matched];
		if (React.isValidElement(element)) {
			parts.push(
				React.cloneElement(element, {
					key: `token-${keyPrefix}-${elementIndex++}`,
				}),
			);
		} else {
			parts.push(element);
		}

		lastIndex = offset + matched.length;
		return matched;
	});

	if (lastIndex < templateString.length) {
		parts.push(templateString.slice(lastIndex));
	}

	return parts;
};

const resolveDefaultLocationType = (codes) => {
	if (!codes || codes.length === 0) return null;
	if (codes.length === 1) {
		return getLocationType(codes[0]);
	}
	return 'group';
};

// Renders a translated title containing subject and location, but wraps them
// with the appropriate elements (subject span, LocationName component).
// templateKey should contain subject/location placeholders.
export default function TitleWithNames({
	subject,
	locations = [],
	templateKey = '{{subject}} for {{location}}',
	suffix = null,
}) {
	const appContext = useAppContext();
	const state = Array.isArray(appContext) ? appContext[0] : appContext;

	// Use unique tokens that won’t appear in normal text
	const subjectToken = '[[SUBJECT]]';
	const locationToken = '[[LOCATION]]';

	const normalizedLocations = (Array.isArray(locations) ? locations : [])
		.map((entry) => {
			if (!entry) return null;

			if (typeof entry === 'string') {
				const code = entry;
				const name = (state && code ? getSelectedLocationName(state, code) : null) || code;
				if (!name) return null;
				return { code, name };
			}

			if (typeof entry === 'object') {
				const explicitName = firstNonEmptyString(entry.name, entry.fullName, entry.label, entry.title);
				const code = firstNonEmptyString(entry.code, entry.iso, entry.uid, entry.id, entry.location);
				const name = explicitName || (state && code ? getSelectedLocationName(state, code) : null) || code;
				if (!name) return null;
				return { code, name };
			}

			return null;
		})
		.filter((item) => item && typeof item.name === 'string' && item.name.trim().length > 0);

	const codesForType = normalizedLocations
		.map((location) => (typeof location.code === 'string' ? location.code : null))
		.filter(Boolean);

	const fallbackLocationType = resolveDefaultLocationType(codesForType);

	const locationElements = normalizedLocations.map((entry, index) => {
		const type = entry.code ? getLocationType(entry.code) : fallbackLocationType;
		return (
			<LocationName type={type} key={`location-${index}`}>
				{entry.name}
			</LocationName>
		);
	});

	const formatLocationElements = (keyPrefix = '') => {
		const count = locationElements.length;
		if (count === 0) {
			return null;
		}

		if (count === 1) {
			const element = locationElements[0];
			return React.cloneElement(element, {
				key: `location-${keyPrefix}-0`,
			});
		}

		if (count === 2) {
			const firstToken = '[[FIRST_LOCATION]]';
			const secondToken = '[[SECOND_LOCATION]]';
			const template = i18n.t('{{firstLocation}} and {{secondLocation}}', {
				firstLocation: firstToken,
				secondLocation: secondToken,
			});
			return replaceTokensWithElements(
				template,
				{
					[firstToken]: locationElements[0],
					[secondToken]: locationElements[1],
				},
				`${keyPrefix}-pair`,
			);
		}

		const othersCount = count - 1;
		const firstElement = React.cloneElement(locationElements[0], {
			key: `location-${keyPrefix}-0`,
		});
		const placeholder = '__FIRST__';
		const summaryTemplate = i18n.t('{{location}}, and {{other_count}} other', {
			location: placeholder,
			other_count: othersCount,
			count: othersCount,
		});
		const summaryText = summaryTemplate.replace(', and', ' and').replace(placeholder, '');
		const suffixText = summaryText.startsWith(' ') ? summaryText : ` ${summaryText}`;

		return [firstElement, <React.Fragment key={`summary-${keyPrefix}`}>{suffixText}</React.Fragment>];
	};

	const template = i18n.t(templateKey, {
		subject: subjectToken,
		location: locationToken,
	});

	const parts = [];
	const regex = /\[\[(SUBJECT|LOCATION)\]\]/g;
	let lastIndex = 0;
	let match;
	let key = 0;

	while ((match = regex.exec(template)) !== null) {
		if (match.index > lastIndex) {
			parts.push(template.slice(lastIndex, match.index));
		}
		if (match[1] === 'SUBJECT') {
			parts.push(
				<span className="subject-name" key={`s-${key++}`}>
					{subject}
				</span>,
			);
		} else {
			const formattedLocations = formatLocationElements(key);
			if (Array.isArray(formattedLocations)) {
				formattedLocations.forEach((item, arrayIndex) => {
					parts.push(
						React.isValidElement(item) ? (
							item
						) : (
							<React.Fragment key={`literal-${key}-${arrayIndex}`}>{item}</React.Fragment>
						),
					);
				});
			} else if (formattedLocations) {
				parts.push(
					React.isValidElement(formattedLocations) ? (
						formattedLocations
					) : (
						<React.Fragment key={`literal-${key}-0`}>{formattedLocations}</React.Fragment>
					),
				);
			}
			key += 1;
		}
		lastIndex = regex.lastIndex;
	}

	if (lastIndex < template.length) {
		parts.push(template.slice(lastIndex));
	}

	if (suffix) parts.push(suffix);

	return <>{parts}</>;
}
